angular.module('leanlaw.core').factory('entryEditService', ['userService', 'clientMatterManager', 'chargeManager', 'alerts', 'lists', function (userService, clientMatterManager, chargeManager, alerts, lists) {
  let _services = {
    updateHours,
    roundToCeilStep,
    getStep,
    getMinuteStep,
    updateStep,
    resetStep,
    updateStepFromEntry,
    updateEndTime,
    updateDescriptionFromTask,
    adjustRateToSelection,
    descriptionHours,
    getChargeList,
    adjustRateToCompletionBasedSelection,
    getFixedFeeList
  };
  let localStep;
  let localDefaultStep;
  let enforceRounding;
  function activate() {
    userService.me().then(me => {
      localDefaultStep = me.company.settings.timeEntryIncrement || 0.1;
      enforceRounding = me.company.settings.enforceRounding || false;
      updateStep(localDefaultStep);
    });
  }
  activate();
  async function adjustRateToSelection(refreshRate, entry, account, vm, preserveBillingType) {
    const {
      rate,
      matter
    } = entry;
    if (account) {
      vm.rateList = vm.accounts?.find(u => u?.id === account.id)?.rateList;
    } else {
      vm.rateList = [];
    }
    if (!refreshRate) {
      // Make the selected rate reflect the rate on the time entry
      const {
        label,
        amount
      } = rate || {};
      if (!matter) {
        vm.selectedRate = vm.rateList?.find(r => r.label === label && r.amount === amount);
        return;
      }
      vm.loadingEntryBillingTypes = true;
      // Return the promise so callers can await the rate being applied.
      return clientMatterManager.getMatter(matter.id).then(async m => {
        await setAvailableBillingTypes(m.billing?.type, vm);
        if (m.rateGroup && vm.me.company.settings.isRateGroupEnabled) {
          updateRateList(vm, account, m, entry);
        } else {
          if (entry.rate?.edited) {
            vm.selectedRate = entry.rate;
          } else {
            vm.selectedRate = vm.rateList?.find(r => r.label === label && r.amount === amount);
          }
        }
        vm.loadingEntryBillingTypes = false;
      });
    }
    if (matter && account) {
      vm.loadingEntryBillingTypes = true;
      const ratePromise = clientMatterManager.getMatter(matter.id).then(async m => {
        await setAvailableBillingTypes(m.billing?.type, vm);
        vm.rateList = vm.rateList?.filter(r => !r.rateGroup);

        // Handle matter-specific setup that should always run
        if (m.billing?.type === 'fixed') {
          await getChargeList(m, vm, entry);
        }

        // Attempt to restore duplicate entry billing choices before applying defaults,
        let duplicateRestored = false;
        if (entry.duplicate && entry.copyBillingType) {
          const foundBillingType = vm.entryBillingTypes?.find(t => t.id === entry.copyBillingType);
          if (foundBillingType) {
            vm.billingType = foundBillingType;
            duplicateRestored = true;

            // For fixed fee duplicates, also restore the specific charge
            if (entry.copyBillingType === 'fixedFee' && entry.copyChargeId && vm.fixedFeeList) {
              const foundCharge = vm.fixedFeeList.find(charge => charge.id === entry.copyChargeId);
              if (foundCharge) {
                vm.fixedFee = {
                  id: foundCharge.id
                };
              }
            }
          }
        }

        // Only set defaults if duplicate wasn't restored and billing type
        // wasn't explicitly chosen by the user (e.g. switching from nonBillable)
        if (!duplicateRestored && !preserveBillingType) {
          if (m.billing?.type === 'fixed') {
            vm.billingType = vm.entryBillingTypes?.find(t => t.id === 'fixedFee');
            if (vm.fixedFeeList.length === 1) {
              const fixedFeeItem = vm.fixedFeeList[0];
              // setting vm.fixedFee = {} to avoid issue with vm.fixedFee.id = undefined
              vm.fixedFee = {};
              vm.fixedFeeList[0].display = fixedFeeItem.displayName || fixedFeeItem.label || fixedFeeItem.description;
              vm.fixedFee.id = fixedFeeItem.id;
            }
          } else if (m.billing?.type === 'contingency' || m.billing?.type === 'internal') {
            // Default to nonBillable for internal/contingency
            vm.billingType = vm.entryBillingTypes?.find(t => t.id === 'nonBillable');
          } else if (m.billing?.type === 'none') {
            vm.billingType = vm.entryBillingTypes?.find(t => t.id === 'nonBillable');
          } else {
            // Hourly matters default to billable
            vm.billingType = vm.entryBillingTypes?.find(t => t.id === 'billable');
          }
        }
        entry.billingType = vm.billingType?.id;

        // For duplicates with an edited rate, preserve the original rate
        if (entry.duplicate && entry.rate?.edited) {
          vm.selectedRate = vm.rateList?.find(r => r.label === entry.rate.label && r.amount === entry.rate.amount);
          vm.loadingEntryBillingTypes = false;
          return;
        }

        // Make the rate reflect the selected matter/account rate
        const matterUser = m.users.find(u => u.id === account.id);
        if (matterUser) {
          if (m.rateGroup && vm.me.company.settings.isRateGroupEnabled) {
            const rateGroupAmount = m.rateGroup.rateGroupAccounts.find(u => u.accountId === account.id)?.rate;
            let rateGroupLabel = m.rateGroup.name;
            if (!vm.rateList.find(item => item.label === rateGroupLabel)) {
              vm.rateList.push({
                label: rateGroupLabel,
                amount: rateGroupAmount,
                rateGroup: true
              });
            }
            vm.selectedRate = vm.rateList?.find(r => r.label === rateGroupLabel && r.amount === rateGroupAmount);
            entry.rate = {
              ...vm.selectedRate
            };
          } else {
            vm.selectedRate = matterUser.rate?.label && vm.rateList.find(r => r.label === matterUser.rate?.label);
            entry.rate = {
              ...matterUser.rate
            };
          }
        } else {
          // The user isn't assigned to the matter which means we are auto-assigning
          vm.selectedRate = vm.rateList?.find(r => r.label === 'Standard');
          entry.rate = {
            ...vm.selectedRate
          };
        }
      });
      vm.loadingEntryBillingTypes = false;
      // Return the promise so callers can await the new matter's rate being
      // fetched and applied to the entry before allowing a save.
      return ratePromise;
    } else {
      entry.rate = {};
    }
    vm.loadingEntryBillingTypes = false;
  }
  async function setAvailableBillingTypes(matterBillingType, vm) {
    if (matterBillingType === 'contingency' || matterBillingType === 'internal') {
      vm.entryBillingTypes = vm.entryBillingTypes.filter(b => b.id !== 'billable');
    } else {
      vm.entryBillingTypes = angular.copy(lists.entryBillingTypes);
    }
  }
  async function getChargeList(m, vm, entry) {
    if (!m?.id) {
      vm.fixedFeeList = [];
      return;
    }
    const billedState = entry.billedState;
    if (!billedState || billedState === 'WIP') {
      await getFixedFeeList(vm, m);
      // if there is only one charge, display dosen't update
    } else if (entry.charge) {
      vm.fixedFeeList = [{
        display: entry.charge?.displayName || entry.charge?.label || entry.charge?.description,
        id: entry.charge?.id
      }];
    }
  }
  async function getFixedFeeList(vm, matter) {
    try {
      const charges = await chargeManager.listForMatter(matter.id, {
        acceptsTime: true
      });
      for (let charge of charges) {
        charge.display = charge.displayName || charge.label || charge.description;
      }
      vm.fixedFeeList = charges.sort((a, b) => {
        return a.display.localeCompare(b.display);
      });
    } catch (e) {
      alerts.error('Error loading fixed fee list');
    }
  }
  function updateRateList(vm, account, m, entry) {
    const amount = m.rateGroup.rateGroupAccounts?.find(u => u.accountId === account.id)?.rate;
    let label = m.rateGroup.name;
    if (!vm.rateList?.find(item => item.label === label)) {
      vm.rateList.push({
        label,
        amount,
        rateGroup: true
      });
    }
    if (entry?.id) {
      const {
        rate
      } = entry;
      const {
        label,
        amount
      } = rate || {};
      vm.selectedRate = vm.rateList?.find(r => r.label === label && r.amount === amount);
    }
    if (!entry?.id) {
      vm.selectedRate = vm.rateList?.find(r => r.label === m.rateGroup.name);
    }
  }
  function descriptionHours(entry, vm) {
    const {
      description,
      id
    } = entry;
    const {
      lockCalculatingHoursFromDescription,
      originalDescription,
      originalHours
    } = vm;

    // if no description or we change hours manually
    if (!description || lockCalculatingHoursFromDescription) return false;

    // if there is no [] with number in description
    const descriptionHoursArray = getDescriptionHoursArray(description);
    if (!descriptionHoursArray.length) return false;

    // if old entry has hours manually enterd
    if (id) {
      if (oldEntryCalculation(originalHours, originalDescription, id)) return false;
    }
    return Number(roundToNearestStep(reduceHours(descriptionHoursArray)));
  }
  function getDescriptionHoursArray(description) {
    const regexp = /\[(?=.)([+-]?(\d*)(\.(\d+))?)\]/g;
    return description.match(regexp) || [];
  }
  function reduceHours(descriptionHoursArray) {
    return descriptionHoursArray.reduce((sum, e) => sum + Number(e.replace(/[()|[\]]/g, '')), 0);
  }
  function oldEntryCalculation(originalHours, originalDescription, id) {
    const originalDescriptionHoursArray = getDescriptionHoursArray(originalDescription);
    const reduceHour = Number(roundToNearestStep(reduceHours(originalDescriptionHoursArray)));
    return id && originalHours !== reduceHour;
  }
  function roundToNearestStep(hours) {
    return (localStep * (hours / localStep)).toFixed(2);
  }
  function getStep() {
    return localStep;
  }
  function getMinuteStep() {
    return localStep * 60;
  }
  function updateHours(entry) {
    if (!entry.endTime) return;
    let hours = (entry.endTime - entry.startTime) / (60 * 60 * 1000);
    // round hours value up to the multiple of default time entry increment
    // set by the firm regardless of the minutes set.
    hours = +roundToCeilStep(hours);
    if (mod(hours, localStep) !== 0) updateStep(hours);
    entry.hours = hours;
  }
  function roundToCeilStep(hours) {
    return (localStep * Math.ceil(hours / localStep)).toFixed(2);
  }
  function updateEndTime(entry) {
    if (!entry.startTime) return;
    entry.endTime = new Date(entry.startTime.getTime() + entry.hours * 60 * 60 * 1000);
  }
  function updateStep(hours) {
    if (enforceRounding || mod(hours, localDefaultStep) === 0) {
      localStep = localDefaultStep;
    } else if (mod(hours, 0.5) === 0) {
      localStep = 0.5;
    } else if (mod(hours, 0.25) === 0) {
      localStep = 0.25;
    } else if (mod(hours, 0.1) === 0) {
      localStep = 0.1;
    } else {
      localStep = 0.01;
    }
  }
  function resetStep() {
    activate();
    updateStep(localDefaultStep);
  }
  function updateStepFromEntry(entry) {
    if (mod(entry.hours, localStep) !== 0) updateStep(entry.hours);
  }
  function updateDescriptionFromTask(entry, task) {
    const {
      description
    } = entry;
    if (description && description.length) {
      if (description[description.length - 1] !== ' ') {
        entry.description += ' ';
      }
      entry.description += task.label;
    } else {
      entry.description = task.label;
    }
  }
  function mod(a, b) {
    // This is necessary to ensure that e.g. mod(0.5,0.1) returns 0.
    // Build in operator result in rounding errors, e.g. 0.5 % 0.1 = 0.99999999999..
    return Math.round(a * 100) % Math.round(b * 100);
  }
  function adjustRateToCompletionBasedSelection(entry, account, vm) {
    if (!account && !entry?.matter || entry.rate?.edited) return;
    vm.disableSave = true;
    clientMatterManager.getMatter(entry.matter.id).then(m => {
      // Make the rate reflect the selected matter/account rate
      const matterUser = m.users.find(u => u.id === account.id);
      if (matterUser) {
        vm.selectedRate = matterUser.rate?.label && vm.rateList.find(r => r.label === matterUser.rate?.label);
        entry.rate = {
          ...matterUser.rate
        };
        vm.disableSave = false;
      } else {
        // The user isn't assigned to the matter which means we are auto-assigning
        vm.selectedRate = vm.rateList?.find(r => r.label === 'Standard');
        entry.rate = {
          ...vm.selectedRate
        };
        vm.disableSave = false;
      }
    });
  }
  return _services;
}]);